
/* eslint-disable no-case-declarations */
import * as $ from 'jquery';

import { AttrUtil } from 'src/integration/api/ui/AttrUtil';
import { Ui } from 'src/integration/api/ui/Ui';
import * as CollectionUtil from 'src/shared/CollectionUtil';
import * as NullUtil from 'src/shared/NullUtil';

import { D2lConfig, D2lPageType } from './D2lConfig';
import { D2lContentItemType } from './D2lContentItemType';
import { ID2lContentItemAnnotatorFactory } from './ID2lContentItemAnnotator';

const contentItemIdRegex = /^d2l_content_[\d]+_([\d]+)$/;

// Variable for assigning array of content items for learner experience
// empty array is assigned to avoid the error of undefined variable
// when D2lContentItem.fromPage is doing for loop
let learnerExperienceBodyHtml: D2lContentItem[] = [];
// ally-content-item is a hidden link that carries the data-ally attributes.
const allyContentItem = '<a id="ally-content-item" href="" style="display: none"></a>';

export class D2lContentItem {

    /**
     * Parse an array of D2L content items from the page.
     *
     * @param config The [D2lConfig], containing the page type we can use to determine how to parse the page
     */
    public static fromPage(config: D2lConfig, $page: JQuery<HTMLElement>): D2lContentItem[] {
        switch (config.pageType) {
            case D2lPageType.TableOfContents:
                // The table of contents page lists a bunch of simple links to content items
                const tableOfContentsTopicLinks = $page
                    .find('ul.d2l-datalist .d2l-topic-view .d2l-link')
                    .toArray()
                    .map((el) => D2lContentItem.fromElement($(el), D2lContentItemType.TableOfContentsTopicLink));

                // It also lists modules and their direct child modules (if any)
                const tableOfContentsModules = $page
                    .find('.d2l-htmlblock')
                    .toArray()
                    .map((el) => D2lContentItem.moduleFromHtmlBlock($(el)));
                return CollectionUtil.compact(tableOfContentsTopicLinks.concat(tableOfContentsModules));
            case D2lPageType.IndividualContentView:
                // Sizzle (jQuery's query selector) sets an id on certain elements that do not have an id of their own.
                // The button bar resides in a div that has no id. This `fromPage` logic runs in a tight loop and seems
                // to have a slight performance impact in Chrome. To avoid this, set an id on the bar once to avoid any
                // further DOM modifications.
                const $btnBar = $page.find('#ContentView').next();
                if (!NullUtil.hasValue($btnBar.attr('id'))) {
                    $btnBar.attr('id', 'd2l_btn_bar');
                }

                // The individual content view page appears holds a download button with an ID that holds the external
                // id for the image
                const $downloadButton = $btnBar.find('.d2l-button[id*="d2l_content_"]');
                const ids = D2lContentItem.extractIdsFromElement($downloadButton);
                if (NullUtil.hasValue(ids)) {
                    const [id, eid] = ids;
                    const contentItems = [];
                    contentItems.push(new D2lContentItem(
                        D2lContentItemType.IndividualContentViewItem,
                        $downloadButton,
                        id,
                        eid
                    ));

                    const $contentViewImage = $page.find('.d2l-fileviewer-image img.d2l-fileviewer-rendered-image');
                    if ($contentViewImage.length > 0) {
                        const imgId = `ally_d2l_${eid}`;
                        $contentViewImage.attr('id', imgId);
                        contentItems.push(new D2lContentItem(
                            D2lContentItemType.ContentViewImage,
                            $contentViewImage,
                            imgId,
                            eid
                        ));
                    }
                    return contentItems;
                } else {
                    return [];
                }
            case D2lPageType.NewContentExperienceView:
                // D2L has provided the d2l_content_<course>_<file> on the content-panel div. The Ally UI api can't
                // handle data-ally attributes on a div however. If an embedded image is present, the image will be
                // used, if there's no image, a hidden link will be added that will carry the data-ally attributes.
                const $contentPanel = $('.content-panel');
                let $a = $contentPanel.find('a#ally-content-item');
                const newContentExperienceIds = D2lContentItem.extractIdsFromElement($contentPanel);
                if (NullUtil.hasValue(newContentExperienceIds)) {
                    const [id, eid] = newContentExperienceIds;

                    // Determine which element will hold the ally attributes. Use the embedded image if it's present,
                    // create a surrogate hidden link otherwise (if it didn't exist already)
                    let $allyEl = $a;
                    const $img = $contentPanel.find('.vui-fileviewer-image-container img.vui-fileviewer-image');
                    if ($img.length === 1) {
                        $allyEl = $img;
                        // The image is lazy loaded into the DOM, there's a high chance that a hidden link was added at
                        // an earlier stage. Remove the hidden link if it's there.
                        $a.remove();
                    } else if ($a.length === 0) {
                        $a = $('<a id="ally-content-item" href="" style="display: none"></a>');
                        $contentPanel.append($a);
                        $allyEl = $a;
                    }

                    //  The user can select a different file without reloading the page, so each time this function
                    // runs, we need to ensure that the correct attributes are present
                    if ($allyEl.attr('data-ally-file-eid') !== eid) {
                        $allyEl.attr('data-ally-file-eid', eid);
                        $allyEl.attr('data-ally-content-id', null);
                    }

                    return [new D2lContentItem(D2lContentItemType.NewContentExperienceView, $allyEl, id, eid)];
                } else {
                    $a.remove();

                    // This codepath can be triggered when a user navigates from a supported file to a module. At that
                    // point there might be an AF link and score indicator in the DOM. The NCEViewAnnotator won't run
                    // after this cycle however, as no supported content is in the DOM anymore. Remove the items that
                    // might've been added prior.
                    $contentPanel.find('.ally-container').remove();

                    return [];
                }
            case D2lPageType.NewLearnerExperienceView:
                // This is a new learner experience view which used for student view.
                // It returns the content items for the learner experience which is fetched from fetchDataUnderShadowRoot()
                    return learnerExperienceBodyHtml;

        }
    }

    /**
     * Given an element and the type of content item it represents, extract its [[D2lContentItem]] instance with the
     * relevant data pulled off the DOM.
     *
     * @param config The D2l config
     * @param $el The DOM element
     * @param type The type of content item instance we expect this to be
     */
    private static fromElement($el: JQuery<HTMLElement>, type: D2lContentItemType): D2lContentItem | null {
        const ids = D2lContentItem.extractIdsFromElement($el);
        if (NullUtil.hasValue(ids)) {
            const [id, eid] = ids;
            return new D2lContentItem(type, $el, id, eid);
        } else {
            return null;
        }
    }

   /**
    * Fetch the iframe element under shadow root and construct the content items for learner experience
    * This function is called in D2lAnnotator.ts
    *
    * @returns iframe document body which is fetched under shadow roots of new learner experience page
    */
    public static async fetchDataUnderShadowRoot(): Promise<JQuery<HTMLElement>> {
        try {
            // Generating d2l_content_${courseId}_${fileId} and fileId for learner experience
            const newLearnerExperienceIds = D2lContentItem.generateIdForLearnerExperience();
            if (!NullUtil.hasValue(newLearnerExperienceIds)) {
                throw new Error(`generateIdForLearnerExperience returned: ${newLearnerExperienceIds}`);
            }
            const [d2lContentId, fileId] = newLearnerExperienceIds;

            // Iterating the shadow root elements to get the HTML element from the iframe
            const iframeElement = await D2lContentItem.fetchIframeDataUnderShadowRoot();
            if (!iframeElement) {
                throw new Error('iframe element not found');
            }

            // Wait for the iframe to load
            await new Promise<void>((resolve, reject) => {
                const onIframeLoad = () => {
                    const iframeDocument = iframeElement.contentDocument || iframeElement.contentWindow?.document;
                    if (!iframeDocument) {
                        // Remove the event listener if the iframe document is not found
                        iframeElement.removeEventListener('load', onIframeLoad);
                        return reject(new Error('iframe Document not found'));
                    }

                    // Adding the ally-content-item to the iframe
                    D2lContentItem.addingAllyContentItemToIframe(iframeDocument, fileId, d2lContentId);

                    const $newLearnerPanel = $(iframeDocument).find('.d2l-popup-printdownload-button-container');

                    // Assigning the content items for learner experience
                    // This value is stored and used in returning the content items for learner experience
                    // in D2lContentItem.fromPage()
                    learnerExperienceBodyHtml = [
                        new D2lContentItem(
                            D2lContentItemType.NewLearnerExperienceViewItem,
                            $newLearnerPanel,
                            d2lContentId,
                            fileId
                        )
                    ];

                    iframeElement.removeEventListener('load', onIframeLoad);
                    resolve();
                };

                iframeElement.addEventListener('load', onIframeLoad);
            });
            return $(iframeElement.contentDocument!.body);
        }
        catch (error) {
            // Return an empty jQuery object to allow the flow to continue
            return $();
        }
    }


   /**
    * Wait for child nodes to load under shadow roots of new learner experience page.
    * This function is called in fetchDataUnderShadowRoot()
    *
    * @param root DOM element
    * @param expectedSelectors expected selectors in array of strings
    */
    public static async waitForChildNodes(root: Element, expectedSelectors: string[]): Promise<NodeListOf<ChildNode>> {
        return new Promise((resolve) => {
            // Check if the expected child nodes are already present
            if (D2lContentItem.areExpectedChildNodesPresent(root, expectedSelectors)) {
                resolve(root.childNodes);
                return;
            }

            // Wait for mutations in the shadow root to load the child nodes
            const observer = new MutationObserver(() => {
                if (D2lContentItem.areExpectedChildNodesPresent(root, expectedSelectors)) {
                    observer.disconnect();
                    resolve(root.childNodes);
                }
                // If childnode is 'd2l-sequences-content-module' then resolve with empty NodeList to allow the flow to continue
                // as this is in module page, where AF icon will not be there
                else if (root.querySelector('d2l-sequences-content-module') !== null) {
                    observer.disconnect();
                    resolve(document.createDocumentFragment().childNodes); // Resolve with an empty NodeList
                }
            });

            // Start observing the shadow root
            observer.observe(root, {
                'childList': true,
                'subtree': true
            });
        });
    }

    /**
     * Check if some expected child nodes are present under a given root element
     *
     * @param root DOM element
     * @param expectedSelectors expected selectors in array of strings
     * @returns Returns `true` if one of the expected selector is present, `false` otherwise
     */
    public static areExpectedChildNodesPresent(root: Element, expectedSelectors: string[]): boolean {
        return expectedSelectors.some(selector => root.querySelector(selector) !== null);
    }

   /**
    * Generate the id for learner experience i.e d2l_content_${courseId}_${fileId} and file id
    * This function is called in fetchDataUnderShadowRoot()
    *
    * @returns Array of strings which has generated ids
    */
    public static generateIdForLearnerExperience(): [string, string] | null {
            const hrefValue = document.querySelector('d2l-sequence-viewer')?.getAttribute('href');
            if (!hrefValue) {
                throw new Error('href attribute not found on d2l-sequence-viewer');
            }

            // Generating id from href
            const splitDataHref = hrefValue.split('/');
            if (splitDataHref.length < 6) {
                throw new Error('Unexpected href format');
            }

            // Extracting courseId and fileId from href
            const courseId = splitDataHref[3];
            const nestedsplitDataHref = splitDataHref[5].split('?');
            if (nestedsplitDataHref.length === 0) {
                throw new Error('Unexpected href format');
            }

            const fileId: string = nestedsplitDataHref[0] ?? '';
            const d2lContentId = `d2l_content_${courseId}_${fileId}`;

            // Returning the generated id and fileId
            return [d2lContentId, fileId];
    }

   /**
    * Fetch the iframe element under shadow root for new learner experience page
    * This function is called in fetchDataUnderShadowRoot()
    *
    * @returns iframe under shadow root for new learner experience page
    */
    public static async fetchIframeDataUnderShadowRoot() {
            const hostElement = document.querySelector('d2l-sequence-viewer');
            if (!hostElement) {
                throw new Error('Host element "d2l-sequence-viewer" not found');
            }

            await D2lContentItem.waitForShadowRoot(hostElement);
            const firstShadowRoot = hostElement.shadowRoot;
            if (!firstShadowRoot) {
                throw new Error('Shadow root of "d2l-sequence-viewer" not found');
            }

            const nestedHostElement = firstShadowRoot.querySelector('#view-container d2l-sequences-content-router');
            if (!nestedHostElement) {
                throw new Error('Nested host element "d2l-sequences-content-router" not found');
            }
            await D2lContentItem.waitForShadowRoot(nestedHostElement);
            const nestedShadowRoot = nestedHostElement.shadowRoot;
            if (!nestedShadowRoot) {
                throw new Error('Shadow root of "d2l-sequences-content-router" not found');
            }

            // Wait for mutations in nestedShadowRoot to load the child nodes
            // For html format files iframe is loaded in d2l-sequences-content-file-html
            // For other format files iframe is loaded in d2l-sequences-content-file-pdf
            // So waiting for any one of these elements to load
            await D2lContentItem.waitForChildNodes(
                nestedShadowRoot as unknown as Element,
                ['d2l-sequences-content-file-pdf', 'd2l-sequences-content-file-html']
                );

            // Fetching the iframe element from nestedShadowRoot
            // For html format files iframe is loaded in d2l-sequences-content-file-html
            // For other format files iframe is loaded in d2l-sequences-content-file-pdf
            // So using 'or' condition to support any one of these elements
            const targetElement = nestedShadowRoot.querySelector('d2l-sequences-content-file-pdf') ||
            nestedShadowRoot.querySelector('d2l-sequences-content-file-html');

            if (!targetElement) {
                throw new Error('Target element "d2l-sequences-content-file-pdf or d2l-sequences-content-file-html" not found');
            }
            await D2lContentItem.waitForShadowRoot(targetElement);
            const nestedShadowRootEnd = targetElement.shadowRoot;
            if (!nestedShadowRootEnd) {
                throw new Error('Shadow root of "d2l-sequences-content-file-pdf or d2l-sequences-content-file-html" not found');
            }

            // Wait for mutations in nestedShadowRootEnd to load the child nodes
            await D2lContentItem.waitForChildNodes(
                nestedShadowRootEnd as unknown as Element,
                ['div.d2l-sequences-content-container']
                );

            const nestedTargetElement = nestedShadowRootEnd.querySelector('div.d2l-sequences-content-container');
            if (!nestedTargetElement) {
                throw new Error('Nested target element "div.d2l-sequences-content-container" not found');
            }

            const iframeElement = nestedTargetElement.querySelector('iframe');
            if (!iframeElement) {
                throw new Error('Iframe element not found');
            }

            return iframeElement;
    }

    /**
    * Wait for the shadow root to load in new learner experience page
    */
    public static waitForShadowRoot(element: Element): Promise<ShadowRoot> {
        return new Promise((resolve) => {
            if (element.shadowRoot) {
                resolve(element.shadowRoot);
            } else {
                const observer = new MutationObserver(() => {
                    if (element.shadowRoot) {
                        observer.disconnect();
                        resolve(element.shadowRoot);
                    }
                });
                observer.observe(element, { 'childList': true, 'subtree': true, 'attributes': true });
            }
        });
    }

   /**
    * Adding the ally-content-item to the iframe
    * data-ally-content-id and data-ally-file-eid attributes where added to ally-content-item
    * This function is called in fetchDataUnderShadowRoot()
    *
    * @param iframeDocument iframe document which is fetched under shadow root in new Learner Experience
    * @param fileId file id which is used to assign for data-ally-file-eid
    * @param d2lContentId d2l_content_${courseId}_${fileId} which is used to assign for data-ally-content-id
    */
    public static addingAllyContentItemToIframe(iframeDocument: Document, fileId: string, d2lContentId: string) {
        // Fetching the data from iframe and
         // adding the ally-content-item to the iframe
        if (iframeDocument) {
            let $aTag = $(iframeDocument).find('a#ally-content-item');
            let $allyContentElement = $aTag;
            // If the ally-content-item is not present in the iframe, then add the ally-content-item to the iframe
            if ($aTag.length === 0) {
                $aTag = $(allyContentItem);
                $(iframeDocument.body).append($aTag);
                $allyContentElement = $aTag;
            }
            // Adding the data-ally-file-eid and data-ally-content-id to the ally-content-item
            if ($allyContentElement.attr(AttrUtil.getDataAllyAttrName('file-eid')) !== fileId) {
                $allyContentElement.attr(AttrUtil.getDataAllyAttrName('file-eid'), fileId);
                $allyContentElement.attr(AttrUtil.getDataAllyAttrName('content-id'), d2lContentId);
            }
        }
    }

    private static extractIdsFromElement($el: JQuery<HTMLElement>): [string, string] | null {
        const id = $el.attr('id');
        if (typeof id === 'string') {
            const eid = extractRegexGroup(contentItemIdRegex, id, 1);
            if (NullUtil.hasValue(eid)) {
                return [id, eid];
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    /**
     * Convert a `d2l-htmlblock` element into a [[D2lContentItem]]
     *
     * @param $el The DOM element to convert
     */
    private static moduleFromHtmlBlock($el: JQuery<HTMLElement>): D2lContentItem | null {
      const nDataListParents = $el.parents('.d2l-datalist').length;
      if (nDataListParents === 1) {
          // This is a top-level module
          const moduleId = $el.parents('.d2l-collapsepane').find('.d2l-expandcollapse').data('key');
          if (NullUtil.hasValue(moduleId)) {
              const eid = `application/x-module:${moduleId}`;
              return new D2lContentItem(D2lContentItemType.TableOfContentsModule, $el, eid, eid);
          } else {
              return null;
          }
      } else if (nDataListParents === 2) {
          // This is a child module
          const moduleId = $el.closest('.d2l-datalist-item-content')
              .find('d2l-dropdown,d2l-dropdown-context-menu')
              .data('placeholderkey');
          if (NullUtil.hasValue(moduleId)) {
              const eid = `application/x-module:${moduleId}`;
              return new D2lContentItem(D2lContentItemType.TableOfContentsChildModule, $el, eid, eid);
          } else {
              return null;
          }
      } else {
          // A selected child-module
          const placeholderId = $el
            .closest('.d2l-form')
            .siblings('.d2l-placeholder')
            .first()
            .attr('id');
          if (NullUtil.hasValue(placeholderId) && placeholderId.startsWith('placeholderid')) {
              const moduleId = placeholderId.replace(/^placeholderid/, '');
              const eid = `application/x-module:${moduleId}`;
              return new D2lContentItem(D2lContentItemType.TableOfContentsChildModule, $el, eid, eid);
          } else {
              return null;
          }
      }
    }

    /**
     *
     * @param type The [[D2lContentItemType]] of the given element
     * @param $el The DOM element that represents the content item and will be annotated with a `data-ally-content-id`
     *            attribute.
     * @param id The id to use as the ally-content-id
     * @param eid The external id as used in the Ally uploads table
     */
    constructor(
        public readonly type: D2lContentItemType,
        public readonly $el: JQuery<HTMLElement>,
        public readonly id: string,
        public readonly eid: string
    ) {}

    /**
     * Annotate this content item, placing the Ally integration control elements onto the DOM in such a way that running
     * the Ally UI API over the DOM will bind necessary handlers to show/hide and launch functionality such as
     * instructor feedback and alternative formats.
     *
     * A content item will only be annotated once. Further attempts to annotate an item that has already been annotated
     * should bail out performantly.
     *
     * @param config The D2L config
     */
    public annotate(config: D2lConfig, ui: Ui): boolean {
        // Break early if this already has been annotated
        if (this.$el[0].hasAttribute('data-ally-content-id')) {
            return false;
        }

        // All content items will have a content id applied to them
        this.$el.attr('data-ally-content-id', this.id);
        return ID2lContentItemAnnotatorFactory(this.type).annotate(config, this, ui);
    }
}

function extractRegexGroup(regex: RegExp, str: string, i: number): string | null {
    const match = regex.exec(str);
    if (match) {
        return NullUtil.orNull(match[i]);
    } else {
        return null;
    }
}
